import React, { forwardRef, ReactNode, useEffect, useImperativeHandle } from 'react';
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  Resolver,
  SubmitHandler,
  useForm,
  UseFormGetValues,
  UseFormSetValue,
} from 'react-hook-form';
import { ObjectSchema } from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTracking } from '../../tracking';
import { Titles } from "../wizard/Tiltes";
import { Steps } from "../wizard/Steps";

export interface FormRef<TFormValues extends FieldValues> {
  getValues: UseFormGetValues<TFormValues>;
  setValue: UseFormSetValue<TFormValues>;
  submit: (handler: SubmitHandler<TFormValues>) => void;
}

export interface FormProps<TFormValues extends FieldValues, TFormSchema extends FieldValues> {
  defaultValues?: DefaultValues<TFormValues>;
  schema?: ObjectSchema<TFormSchema>;
  onSubmit?: SubmitHandler<TFormValues>;
  onChange?: () => void;
  children?: ReactNode;
  className?: string;
  firstForm?: boolean;
}

export const Form = forwardRef(
  <TFormValues extends FieldValues, TFormSchema extends FieldValues>(
    { onSubmit, onChange, defaultValues, schema, className, firstForm, children }: FormProps<TFormValues, TFormSchema>,
    ref: React.ForwardedRef<FormRef<TFormValues>>,
  ) => {
    const resolver = schema ? yupResolver(schema) : undefined;
    const form = useForm<TFormValues>({
      mode: 'onChange',
      reValidateMode: 'onChange',
      criteriaMode: 'all',
      defaultValues,
      /*
       * typescript type error for yupResolver
       * TODO: fix when issues will be resolved: https://github.com/react-hook-form/resolvers/issues/648
       * */
      resolver: resolver as unknown as Resolver<TFormValues>,
    });

    if(firstForm)
    {
      const trackingService = useTracking();
      useEffect(() => {
        setTimeout(() => {
          trackingService.trackFormPageLoad({
            form: {
              FormStart: firstForm,
              FormName: Titles[Steps.ContactDetails],
            }
          });
        }, 0);
      }, []);
    }
    useImperativeHandle(ref, () => ({
      getValues: form.getValues,
      setValue: form.setValue,
      submit: (handler) => form.handleSubmit(handler)(),
    }));

    return (
      <FormProvider {...form}>
        <form noValidate onChange={onChange} onSubmit={onSubmit && form.handleSubmit(onSubmit)} className={className}>
          {children}
        </form>
      </FormProvider>
    );
  },
);
